import React, { useState, useEffect, useCallback } from "react";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Chart from "../Dashboard/Chart";
import Title from "../Dashboard/Title";
import axios from "axios";
import CsvDownloadButton from "react-json-to-csv";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DashboardTable from "../Dashboard/DashboardTable";
import Pagination from "@mui/material/Pagination";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { Typography } from "@mui/material";

/**
 * Company Deposits Component - Shows only credits without rupees conversion
 * Custom component for company dashboard to hide monetary values
 */
const CompanyDeposits = ({ totalCredits, minDate, maxDate }) => {
  // Format dates for display
  const formatDate = (date) => {
    if (!date) return "N/A";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Determine the title based on date range
  const getTimeframeTitle = () => {
    if (!minDate || !maxDate) return "Total Credits Used";

    // Same month and year
    if (
      minDate.getMonth() === maxDate.getMonth() &&
      minDate.getFullYear() === maxDate.getFullYear()
    ) {
      return `Credits Used (${minDate.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      })})`;
    }

    return "Credits Used (Selected Period)";
  };

  return (
    <React.Fragment>
      <Title>{getTimeframeTitle()}</Title>
      <Typography
        component='p'
        variant='h4'
      >
        {totalCredits.toLocaleString()}
      </Typography>
      <Typography
        color='text.secondary'
        sx={{ flex: 1 }}
      >
        Credits Used This Period
      </Typography>
      <div>
        <Typography
          color='text.secondary'
          sx={{ flex: 1, mt: 1 }}
        >
          {formatDate(minDate)} - {formatDate(maxDate)}
        </Typography>
      </div>
    </React.Fragment>
  );
};

/**
 * Company Room Bookings Component - Displays company-specific room booking data
 *
 * Shows credits usage, booking trends, and detailed booking information
 * filtered specifically for the company admin's company
 *
 * @param {Object} props - Component props
 * @param {number} props.companyId - The company ID to filter bookings by
 * @returns {JSX.Element} - The rendered company room bookings component
 */
function CompanyRoomBookings({ companyId }) {
  const [bookings, setBookings] = useState([]);
  const [totalCredits, setTotalCredits] = useState(0);
  const [creditsByDate, setCreditsByDate] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBookings, setTotalBookings] = useState(0);
  const [allBookings, setAllBookings] = useState([]);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [userDetails, setUserDetails] = useState(null);

  // Get current date for default values
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth(); // 0-11

  // Calculate 6 months from today
  const sixMonthsFromToday = new Date();
  sixMonthsFromToday.setMonth(sixMonthsFromToday.getMonth() + 6);
  const maxYear = sixMonthsFromToday.getFullYear();
  const maxMonth = sixMonthsFromToday.getMonth();

  // State for selected year and month
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  // Compute start and end dates based on selected year and month
  const [startDate, setStartDate] = useState(() => {
    return new Date(currentYear, currentMonth, 1);
  });

  const [endDate, setEndDate] = useState(() => {
    return new Date(currentYear, currentMonth + 1, 0);
  });

  const [loading, setLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // API endpoints
  const getEventsApi = process.env.REACT_APP_API_URL + "/api/get-events-list";
  const getCompanyDetailsApi =
    process.env.REACT_APP_API_URL + "/api/get-company-details";
  const getUserApi = process.env.REACT_APP_API_URL + "/api/get-user";

  // Generate arrays of available years and month names
  const years = Array.from({ length: maxYear - 2024 + 1 }, (_, i) => 2024 + i);
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  /**
   * Gets the JWT token from cookies for API authentication
   * @returns {string|null} - The JWT token or null if not found
   */
  const getAuthToken = () => {
    const cookies = document.cookie.split(";");
    let token = null;

    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === "_auth") {
        token = decodeURIComponent(value);
        break;
      }
    }

    return token;
  };

  /**
   * Formats a Date using local calendar fields for API date filters.
   * Avoids UTC conversion shifts from toISOString() on month boundaries.
   */
  const formatDateForApi = useCallback((date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }, []);

  /**
   * Check if user is a company admin (type_id >= 2)
   * @returns {boolean} - True if user has company admin privileges
   */
  const isCompanyAdmin = useCallback(() => {
    return userDetails && userDetails.user_type >= 2;
  }, [userDetails]);

  /**
   * Updates the date range based on selected year and month
   * @param {number} year - Selected year
   * @param {number} month - Selected month (0-11)
   */
  const updateDateRange = useCallback((year, month) => {
    const newStartDate = new Date(year, month, 1);
    const newEndDate = new Date(year, month + 1, 0);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  }, []);

  /**
   * Handler for year change
   */
  const handleYearChange = (event) => {
    const newYear = event.target.value;
    setSelectedYear(newYear);

    if (newYear === 2024 && selectedMonth < 2) {
      setSelectedMonth(2); // March
      updateDateRange(newYear, 2);
    } else if (newYear === maxYear && selectedMonth > maxMonth) {
      setSelectedMonth(maxMonth);
      updateDateRange(newYear, maxMonth);
    } else {
      updateDateRange(newYear, selectedMonth);
    }
    setPage(1);
    setIsInitialLoad(true);
  };

  /**
   * Handler for month change
   */
  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    setSelectedMonth(newMonth);
    updateDateRange(selectedYear, newMonth);
    setPage(1);
    setIsInitialLoad(true);
  };

  /**
   * Creates empty chart data for when no bookings exist
   */
  const createEmptyChartData = useCallback(() => {
    const emptyData = [];
    const currentDate = new Date(startDate);
    const tempEndDate = new Date(endDate);

    while (currentDate <= tempEndDate) {
      emptyData.push({
        date: currentDate.toLocaleDateString(),
        credits: 0,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return emptyData;
  }, [startDate, endDate]);

  /**
   * Processes bookings data to calculate chart data (credits by date)
   */
  const processChartData = useCallback(
    (bookingsData) => {
      const creditsSum = bookingsData.reduce(
        (sum, booking) => sum + (booking.credits || 0),
        0
      );
      setTotalCredits(creditsSum);

      // Process credits by date for chart
      const creditsByDay = {};

      // Initialize each day of the month with 0 credits
      const currentDate = new Date(startDate);
      const tempEndDate = new Date(endDate);

      while (currentDate <= tempEndDate) {
        const dateKey = currentDate.toLocaleDateString();
        creditsByDay[dateKey] = 0;
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Add credits from bookings
      if (bookingsData && bookingsData.length > 0) {
        bookingsData.forEach((booking) => {
          if (booking && booking.start) {
            const date = new Date(booking.start).toLocaleDateString();
            creditsByDay[date] =
              (creditsByDay[date] || 0) + (booking.credits || 0);
          }
        });
      }

      // Convert to array format for chart
      const creditsByDateArray = Object.keys(creditsByDay).map((date) => ({
        date,
        credits: creditsByDay[date],
      }));

      // Sort by date
      creditsByDateArray.sort((a, b) => new Date(a.date) - new Date(b.date));

      return creditsByDateArray;
    },
    [startDate, endDate]
  );

  /**
   * Format bookings for display in the table
   */
  const formatBookingsForDisplay = useCallback((bookingsData) => {
    return bookingsData.map((booking) => ({
      ...booking,
      end: new Date(booking.end).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }),
      start: new Date(booking.start).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }),
      date: new Date(booking.start).toLocaleDateString(),
    }));
  }, []);

  /**
   * Fetch user details to determine user type and permissions
   */
  const fetchUserDetails = useCallback(async () => {
    try {
      const token = getAuthToken();

      if (!token) {
        setError("Authentication required. Please log in again.");
        return;
      }

      // Try multiple sources for user ID
      let userId = null;

      // First, try localStorage authUser
      try {
        const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
        userId = authUser.uid;
      } catch (e) {
        // Silent fail, try JWT decode
      }

      // If no userId from localStorage, try to decode JWT token
      if (!userId) {
        try {
          // Simple JWT decode (just for getting user ID, not for verification)
          const tokenParts = token.split(".");
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            userId = payload.sub || payload.user_id || payload.uid;
          }
        } catch (e) {
          // Silent fail
        }
      }

      if (!userId) {
        setError("User session invalid. Please log in again.");
        return;
      }

      const response = await axios.get(`${getUserApi}?uid=${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });

      if (response.data && response.data.length > 0) {
        setUserDetails(response.data[0]);
      } else {
        setError("User details not found. Please log in again.");
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      if (error.response && error.response.status === 401) {
        setError("Your session has expired. Please log in again.");
      } else {
        setError("Failed to load user details. Please try again.");
      }
    }
  }, [getUserApi]);

  /**
   * Fetch company details
   */
  const fetchCompanyDetails = useCallback(async () => {
    if (!companyId) return;

    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${getCompanyDetailsApi}?companyId=${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (response.data && response.data.success) {
        setCompanyDetails(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching company details:", error);
    }
  }, [companyId, getCompanyDetailsApi]);

  /**
   * Fetch initial data for the selected date range
   */
  const fetchInitialData = useCallback(async () => {
    if (loading || chartLoading) {
      return;
    }

    setLoading(true);
    setChartLoading(true);
    setError(null);

    try {
      const token = getAuthToken();

      const formattedStartDate = formatDateForApi(startDate);
      const formattedEndDate = formatDateForApi(endDate);

      // Determine which API parameter to use based on user type
      const apiParams = {
        page: 1,
        limit: 1000,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      };

      // Use companyEvents for company admins, myEvents for regular users
      if (isCompanyAdmin()) {
        apiParams.companyEvents = true;
      } else {
        apiParams.myEvents = true;
      }

      const response = await axios.get(getEventsApi, {
        params: apiParams,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (!response.data || !response.data.data) {
        throw new Error("Unexpected API response format");
      }

      const allBookingsData = response.data.data;
      setAllBookings(allBookingsData);

      const total = allBookingsData.length;
      setTotalBookings(total);
      setTotalPages(Math.ceil(total / limit) || 1);

      // Process chart data
      const chartData = processChartData(allBookingsData);
      setCreditsByDate(chartData);

      // Get paginated data for the current page
      const startIndex = (page - 1) * limit;
      const endIndex = Math.min(startIndex + limit, allBookingsData.length);
      const paginatedBookings = allBookingsData.slice(startIndex, endIndex);

      const formattedBookings = formatBookingsForDisplay(paginatedBookings);
      setBookings(formattedBookings);
    } catch (error) {
      console.error("Error fetching data:", error);

      if (error.response && error.response.status === 401) {
        setError("Your session has expired. Please log in again.");
      } else if (error.response && error.response.status === 403) {
        setError(
          "You don't have permission to view company bookings. Company admin access required."
        );
      } else if (error.message) {
        setError(`Error: ${error.message}`);
      } else {
        setError("Failed to load data. Please try again later.");
      }

      setAllBookings([]);
      setBookings([]);
      setTotalPages(0);
      setTotalBookings(0);
      setCreditsByDate(createEmptyChartData());
      setTotalCredits(0);
    } finally {
      setLoading(false);
      setChartLoading(false);
      setIsInitialLoad(false);
    }
  }, [
    startDate,
    endDate,
    getEventsApi,
    limit,
    page,
    processChartData,
    formatBookingsForDisplay,
    formatDateForApi,
    loading,
    chartLoading,
    createEmptyChartData,
    isCompanyAdmin,
  ]);

  /**
   * Updates pagination without making a new API call
   */
  const updatePagination = useCallback(() => {
    if (allBookings.length === 0) return;

    const startIndex = (page - 1) * limit;
    const endIndex = Math.min(startIndex + limit, allBookings.length);
    const paginatedBookings = allBookings.slice(startIndex, endIndex);

    const formattedBookings = formatBookingsForDisplay(paginatedBookings);
    setBookings(formattedBookings);

    setTotalPages(Math.ceil(allBookings.length / limit) || 1);
  }, [allBookings, page, limit, formatBookingsForDisplay]);

  // Effects
  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  useEffect(() => {
    fetchCompanyDetails();
  }, [fetchCompanyDetails]);

  useEffect(() => {
    if (isInitialLoad && userDetails) {
      fetchInitialData();
    }
  }, [fetchInitialData, isInitialLoad, userDetails]);

  useEffect(() => {
    if (!isInitialLoad) {
      updatePagination();
    }
  }, [page, limit, updatePagination, isInitialLoad]);

  // Event handlers
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
    setPage(1);
  };

  // Table configuration
  const columns = [
    "Date",
    "Start Time",
    "End Time",
    "Room",
    "Description",
    "Email",
    "Credits",
  ];

  const columnMapping = {
    Date: "date",
    "Start Time": "start",
    "End Time": "end",
    Room: "roomName",
    Description: "description",
    Email: "email",
    Credits: "credits",
  };

  if ((loading || chartLoading) && !bookings.length) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {error && (
        <Alert
          severity='error'
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* Date Range Selectors */}
      <Box sx={{ display: "flex", gap: 2, mb: 4, flexWrap: "wrap" }}>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={selectedYear}
            label='Year'
            onChange={handleYearChange}
          >
            {years.map((year) => (
              <MenuItem
                key={year}
                value={year}
              >
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Month</InputLabel>
          <Select
            value={selectedMonth}
            label='Month'
            onChange={handleMonthChange}
          >
            {months.map((month, index) => {
              const isDisabled =
                (selectedYear === 2024 && index < 2) ||
                (selectedYear === maxYear && index > maxMonth);
              return (
                <MenuItem
                  key={index}
                  value={index}
                  disabled={isDisabled}
                >
                  {month}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>

      <Grid
        container
        spacing={3}
      >
        {/* Credits Usage Chart */}
        <Grid
          item
          xs={12}
          md={8}
          lg={9}
        >
          <Paper
            sx={{ p: 2, display: "flex", flexDirection: "column", height: 240 }}
          >
            <Title>
              Company Credits Usage - {months[selectedMonth]} {selectedYear}
            </Title>
            {chartLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Chart creditsByDate={creditsByDate} />
            )}
          </Paper>
        </Grid>

        {/* Total Credits Summary */}
        <Grid
          item
          xs={12}
          md={4}
          lg={3}
        >
          <Paper
            sx={{ p: 2, display: "flex", flexDirection: "column", height: 240 }}
          >
            <CompanyDeposits
              totalCredits={totalCredits}
              minDate={startDate}
              maxDate={endDate}
            />
          </Paper>
        </Grid>

        {/* Bookings Table */}
        <Grid
          item
          xs={12}
        >
          <Paper
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              width: "100%",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Title>Company Bookings ({totalBookings} total)</Title>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <CsvDownloadButton
                  data={allBookings.map((booking) => ({
                    ...booking,
                    date: new Date(booking.start).toLocaleDateString(),
                    startTime: new Date(booking.start).toLocaleTimeString(),
                    endTime: new Date(booking.end).toLocaleTimeString(),
                  }))}
                  filename={`${companyDetails?.name || "Company"}_Bookings_${
                    months[selectedMonth]
                  }_${selectedYear}.csv`}
                  style={{ background: "none", border: "none", padding: "0px" }}
                >
                  <FileDownloadOutlinedIcon />
                </CsvDownloadButton>

                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>Rows per page</InputLabel>
                  <Select
                    value={limit}
                    label='Rows per page'
                    onChange={handleLimitChange}
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <DashboardTable
                  data={bookings}
                  columns={columns}
                  columnMapping={columnMapping}
                />

                {totalPages > 1 && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 2 }}
                  >
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color='primary'
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CompanyRoomBookings;
